import { HelpCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks/hooks/use-mobile";
import { ErrorAlert } from "@/lib/components/error-alert";
import { BackButton, BaseNavigationBar } from "@/lib/components/navigation";
import { FormActionButtons, NavigationGuardProvider } from "@/lib/components/navigation-guard";
import { PageContent } from "@/lib/components/page-content";
import { useSalonStore } from "@/lib/store/salon";
import { SettingsSearchTrigger } from "../../search/components/settings-search-trigger";
import { NotificationUsageCard } from "./components/cards/notification-usage-card";
import {
  ChannelToggleChange,
  GeneralChannelSettings,
  GeneralChannelSettingsCard,
  PhoneProvider,
  setPhoneProvidersEnabled,
} from "./components/general-channel-settings";
import { NotificationType } from "./components/notification-type-selector";
import {
  ChannelUsageStats,
  UpdateMultiNotificationSettingsInput,
  useMultiNotificationSettings,
  useUpdateMultiNotificationSettings,
} from "./hooks/use-notification-settings";

export function MultiNotificationsSettingsPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { t } = useTranslation("settings");
  const { currentSalon } = useSalonStore();
  const { data, loading, error } = useMultiNotificationSettings();
  const [updateSettings] = useUpdateMultiNotificationSettings();

  // No need for selectedType since we show both cards

  const [notificationSettings, setNotificationSettings] = useState<
    Record<NotificationType, GeneralChannelSettings>
  >({} as any);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingToggleTypes, setSavingToggleTypes] = useState<
    Partial<Record<NotificationType, boolean>>
  >({});
  const [originalSettings, setOriginalSettings] = useState<
    Record<NotificationType, GeneralChannelSettings>
  >({} as any);
  const originalSettingsRef = useRef(originalSettings);
  originalSettingsRef.current = originalSettings;

  // Initialize form state when data loads - convert from chain format to simple channel settings
  useEffect(() => {
    if (data?.getMultiNotificationSettings?.notificationTypes) {
      const settingsMap: Record<NotificationType, GeneralChannelSettings> = {} as any;

      data.getMultiNotificationSettings.notificationTypes.forEach((typeSettings) => {
        // Since we now have only one general chain config per type, get the first one
        const generalConfig = typeSettings.chainConfigs[0];

        if (generalConfig?.chainSettings) {
          const chainSettings = generalConfig.chainSettings;

          // Convert chain settings to phone providers structure
          const phoneProviders: PhoneProvider[] = [
            {
              id: "whatsapp",
              type: "whatsapp",
              enabled: chainSettings.phoneProviders.some((p) => p.type === "whatsapp" && p.enabled),
              order: chainSettings.phoneProviders.find((p) => p.type === "whatsapp")?.order || 1,
              draggable:
                chainSettings.phoneProviders.find((p) => p.type === "whatsapp")?.draggable !==
                false,
              name: "WhatsApp",
              priority: "Priority: 1st",
            },
            {
              id: "telegram",
              type: "telegram",
              enabled: chainSettings.phoneProviders.some((p) => p.type === "telegram" && p.enabled),
              order: chainSettings.phoneProviders.find((p) => p.type === "telegram")?.order || 2,
              draggable:
                chainSettings.phoneProviders.find((p) => p.type === "telegram")?.draggable !==
                false,
              name: "Telegram",
              priority: "Priority: 2nd",
            },
            {
              id: "sms",
              type: "sms",
              enabled: chainSettings.phoneProviders.some((p) => p.type === "sms" && p.enabled),
              order: chainSettings.phoneProviders.find((p) => p.type === "sms")?.order || 3,
              draggable:
                chainSettings.phoneProviders.find((p) => p.type === "sms")?.draggable !== false,
              name: "SMS",
              priority: "Final fallback",
            },
          ].sort((a, b) => a.order - b.order);

          const hasAnyPhoneProvider = phoneProviders.some((p) => p.enabled);

          settingsMap[typeSettings.type as NotificationType] = {
            emailEnabled: chainSettings.emailEnabled,
            phoneProvidersEnabled: hasAnyPhoneProvider,
            phoneProviders,
          };
        }
      });

      // A refetch (e.g. after a toggle auto-save) must not wipe unsaved edits:
      // keep the local settings of every type that differs from its saved state.
      setNotificationSettings((current) => {
        const merged = { ...settingsMap };
        for (const type of Object.keys(settingsMap) as NotificationType[]) {
          const local = current[type];
          const saved = originalSettingsRef.current[type];
          if (local && saved && JSON.stringify(local) !== JSON.stringify(saved)) {
            merged[type] = local;
          }
        }
        return merged;
      });
      setOriginalSettings(JSON.parse(JSON.stringify(settingsMap))); // Deep clone for comparison
    }
  }, [data]);

  // Check for changes
  useEffect(() => {
    if (Object.keys(originalSettings).length === 0) return;

    const hasChangesDetected =
      JSON.stringify(notificationSettings) !== JSON.stringify(originalSettings);
    setHasChanges(hasChangesDetected);
  }, [notificationSettings, originalSettings]);

  const handleChannelSettingsChange = (
    notificationType: NotificationType,
    settings: GeneralChannelSettings
  ) => {
    setNotificationSettings((prev) => ({
      ...prev,
      [notificationType]: settings,
    }));
  };

  const handleToggle = async (notificationType: NotificationType, change: ChannelToggleChange) => {
    const persisted = originalSettings[notificationType];
    if (!persisted) return;

    const nextPersisted = applyToggleChange(persisted, change);
    setOriginalSettings((current) => ({ ...current, [notificationType]: nextPersisted }));
    setSavingToggleTypes((current) => ({ ...current, [notificationType]: true }));
    try {
      const result = await updateSettings({
        variables: {
          salonId: currentSalon?.id ?? "",
          input: buildNotificationInput(notificationType, nextPersisted),
        },
      });
      if (result.errors?.length) {
        throw new Error(result.errors[0].message);
      }
      toast.success(t("messages.toggleSaved"));
    } catch {
      setOriginalSettings((current) => ({ ...current, [notificationType]: persisted }));
      setNotificationSettings((current) => ({
        ...current,
        [notificationType]: revertToggleChange(current[notificationType], persisted, change),
      }));
      toast.error(t("messages.toggleSaveFailed"));
    } finally {
      setSavingToggleTypes((current) => ({ ...current, [notificationType]: false }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save all changed notification types
      for (const [type, settings] of Object.entries(notificationSettings)) {
        const notificationType = type as NotificationType;
        const originalTypeSettings = originalSettings[notificationType];

        // Check if this type has changes
        if (JSON.stringify(settings) === JSON.stringify(originalTypeSettings)) {
          continue; // Skip unchanged types
        }

        // Convert phone providers settings back to chain format
        // If phone providers are disabled, disable all individual providers
        // Note: Only send fields that match GraphQL PhoneProviderSettingInput schema
        const phoneProviders = settings.phoneProviders.map((provider) => ({
          type: provider.type,
          enabled: settings.phoneProvidersEnabled && provider.enabled,
          order: provider.order,
        }));

        // Build chain updates for this type (now just one general chain)
        const chainUpdates = [
          {
            chainKey:
              notificationType === NotificationType.CUSTOMER
                ? "general_customer_chain"
                : "general_user_chain",
            chainSettings: {
              emailEnabled: settings.emailEnabled,
              phoneProviders,
            },
          },
        ];

        const input: UpdateMultiNotificationSettingsInput = {
          notificationType,
          chainUpdates,
        };

        await updateSettings({
          variables: {
            salonId: currentSalon?.id ?? "",
            input,
          },
        });
      }

      toast.success(
        t("notifications.settings.saveSuccess", {
          defaultValue: "Notification settings saved successfully",
        })
      );

      // Update original settings to current state
      setOriginalSettings(JSON.parse(JSON.stringify(notificationSettings)));
      setHasChanges(false);
    } catch (_err) {
      toast.error(
        t("notifications.settings.saveError", {
          defaultValue: "Failed to save notification settings. Please try again.",
        })
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setNotificationSettings(JSON.parse(JSON.stringify(originalSettings)));
    setHasChanges(false);
  };

  // Header help icon for subtitle/description
  const [headerHelpOpen, setHeaderHelpOpen] = useState(false);
  const headerHelp = (
    <TooltipProvider>
      <Tooltip>
        <Popover open={headerHelpOpen} onOpenChange={setHeaderHelpOpen}>
          <PopoverTrigger asChild>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-auto p-1 text-muted-foreground hover:text-foreground"
                onClick={() => setHeaderHelpOpen(!headerHelpOpen)}
              >
                <HelpCircle className="h-4 w-4" />
                <span className="sr-only">Show help</span>
              </Button>
            </TooltipTrigger>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-4" align="end" side="bottom" sideOffset={4}>
            <div className="text-sm text-muted-foreground leading-relaxed">
              {t("sections.notifications.description")}
            </div>
          </PopoverContent>
        </Popover>
        <TooltipContent side="top" className="max-w-xs">
          <p className="text-xs">{t("common.moreInfo", { defaultValue: "More info" })}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <NavigationGuardProvider
      hasUnsavedChanges={hasChanges}
      onSave={handleSave}
      onDiscard={handleReset}
      saving={saving}
      description={t("messages.unsavedChanges")}
    >
      <BaseNavigationBar
        title={t("sections.notifications.title")}
        subtitle={undefined}
        leadElement={
          isMobile ? <BackButton key="back-button" onBack={() => navigate(-1)} /> : undefined
        }
        trailingElements={[
          <SettingsSearchTrigger key="search-trigger" />,
          headerHelp,
          <FormActionButtons
            key="form-actions"
            isDirty={hasChanges}
            onSave={handleSave}
            onDiscard={handleReset}
            saving={saving}
          />,
        ]}
      />

      <PageContent>
        <div className="space-y-6">
          <ErrorAlert error={error} />

          {!error && (
            <>
              {/* Usage Statistics Card */}
              <NotificationUsageCard
                usage={
                  data?.getMultiNotificationSettings?.usage as {
                    EMAIL: ChannelUsageStats;
                    SMS: ChannelUsageStats;
                    WHATSAPP: ChannelUsageStats;
                    resetDate: string;
                  }
                }
                isLoading={loading}
              />

              {/* Customer Notifications Card */}
              <div className="space-y-6">
                {notificationSettings[NotificationType.CUSTOMER] ? (
                  <GeneralChannelSettingsCard
                    title={t("notifications.types.customer.title", {
                      defaultValue: "Customer Notifications",
                    })}
                    description={t("notifications.types.customer.description", {
                      defaultValue:
                        "Configure how customers receive appointment confirmations, reminders, and updates.",
                    })}
                    settings={notificationSettings[NotificationType.CUSTOMER]}
                    onChange={(settings) =>
                      handleChannelSettingsChange(NotificationType.CUSTOMER, settings)
                    }
                    disabled={saving}
                    onToggle={(change) => void handleToggle(NotificationType.CUSTOMER, change)}
                    togglesSaving={savingToggleTypes[NotificationType.CUSTOMER] ?? false}
                  />
                ) : loading ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>{t("common.loading", { defaultValue: "Loading customer settings..." })}</p>
                  </div>
                ) : null}

                {/* Staff Notifications Card */}
                {notificationSettings[NotificationType.USER] ? (
                  <GeneralChannelSettingsCard
                    title={t("notifications.types.user.title", {
                      defaultValue: "Staff Notifications",
                    })}
                    description={t("notifications.types.user.description", {
                      defaultValue:
                        "Configure how salon staff receive notifications about new appointments, cancellations, and daily summaries.",
                    })}
                    settings={notificationSettings[NotificationType.USER]}
                    onChange={(settings) =>
                      handleChannelSettingsChange(NotificationType.USER, settings)
                    }
                    disabled={saving}
                    onToggle={(change) => void handleToggle(NotificationType.USER, change)}
                    togglesSaving={savingToggleTypes[NotificationType.USER] ?? false}
                  />
                ) : loading ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>{t("common.loading", { defaultValue: "Loading staff settings..." })}</p>
                  </div>
                ) : null}

                {/* Show error if no settings loaded */}
                {!loading &&
                  !notificationSettings[NotificationType.CUSTOMER] &&
                  !notificationSettings[NotificationType.USER] && (
                    <div className="text-center py-8 text-muted-foreground">
                      <p>
                        {t("notifications.noConfigs", {
                          defaultValue: "No notification configurations available.",
                        })}
                      </p>
                    </div>
                  )}
              </div>
            </>
          )}
        </div>
      </PageContent>
    </NavigationGuardProvider>
  );
}

function applyToggleChange(settings: GeneralChannelSettings, change: ChannelToggleChange) {
  if (change.kind === "email") return { ...settings, emailEnabled: change.enabled };
  if (change.kind === "phoneProviders") return setPhoneProvidersEnabled(settings, change.enabled);
  return {
    ...settings,
    phoneProviders: settings.phoneProviders.map((provider) =>
      provider.id === change.providerId ? { ...provider, enabled: change.enabled } : provider
    ),
  };
}

function revertToggleChange(
  current: GeneralChannelSettings,
  persisted: GeneralChannelSettings,
  change: ChannelToggleChange
) {
  if (change.kind === "email") return { ...current, emailEnabled: persisted.emailEnabled };
  if (change.kind === "phoneProviders") {
    return {
      ...current,
      phoneProvidersEnabled: persisted.phoneProvidersEnabled,
      phoneProviders: current.phoneProviders.map((provider) => ({
        ...provider,
        enabled: persisted.phoneProviders.find((item) => item.id === provider.id)?.enabled ?? false,
      })),
    };
  }
  return {
    ...current,
    phoneProviders: current.phoneProviders.map((provider) =>
      provider.id === change.providerId
        ? {
            ...provider,
            enabled:
              persisted.phoneProviders.find((item) => item.id === provider.id)?.enabled ?? false,
          }
        : provider
    ),
  };
}

function buildNotificationInput(
  notificationType: NotificationType,
  settings: GeneralChannelSettings
): UpdateMultiNotificationSettingsInput {
  return {
    notificationType,
    chainUpdates: [
      {
        chainKey:
          notificationType === NotificationType.CUSTOMER
            ? "general_customer_chain"
            : "general_user_chain",
        chainSettings: {
          emailEnabled: settings.emailEnabled,
          phoneProviders: settings.phoneProviders.map((provider) => ({
            type: provider.type,
            enabled: settings.phoneProvidersEnabled && provider.enabled,
            order: provider.order,
          })),
        },
      },
    ],
  };
}
